import { useState, useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import PQueue from 'p-queue';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { MAX_FILE_SIZE_IN_BYTES, PROMO_TEMPLATES_URL } from '@/utils/constants';
import CampaignAssetUploader from '../CampaignAssetUploader';
import AssetPreview from '../AssetPreview';
import {
  CampaignFormModel,
  FileErrorType,
  Status,
} from '../../../models/campaign';
import {
  isSupportedAsset,
  getAdjustedAspectRatioString,
  getSelectedZoneRatios,
  getZoneRatio,
} from '../../../utils';
import { ZonePlacement } from '../../../models/zoneplacement';
import AssetGuidelinesModal from '../AssetGuidelinesModal';
import { AssetMethod } from '../AssetMethod';

type HandleFileErrorFunction = (
  fileErrorType: FileErrorType | 'clear-error',
  fileName: string,
) => void;

type UploadFileFunction = (
  asset: File,
  duration: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => Promise<any>;

interface Props {
  handleFileError: HandleFileErrorFunction;
  uploadFile: UploadFileFunction;
  uploadFileQueue: PQueue;
  campaignStatus?: Status;
  campaignZonePlacementList: ZonePlacement[] | undefined;
  isReadOnly?: boolean;
  setAssetFromLibrary: (id: number, duration: number) => void;
  onAssetRemoved: () => void;
}

export default function PromotionCreative({
  handleFileError,
  uploadFile,
  uploadFileQueue,
  campaignStatus,
  campaignZonePlacementList,
  isReadOnly,
  setAssetFromLibrary,
  onAssetRemoved,
}: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileDimension, setFileDimension] = useState('');
  const [videoDuration, setVideoDuration] = useState<number | undefined>(
    undefined,
  );
  const { control, watch, setError } = useFormContext<CampaignFormModel>();
  const { field: campaignFileUploadField } = useController({
    name: 'file',
    control,
  });
  const selectedZones = watch('selectedZones') ?? [];
  const selectedZonePlacements = watch('selectedZonePlacements');
  const campaignFiles = watch('file');
  const durationList = watch('durationList');
  const isCampaignEnded = campaignStatus === 'completed';
  const noZonesSelected =
    selectedZones?.length === 0 && selectedZonePlacements?.length === 0;

  const handleFileReupload = useCallback(
    async (newDuration: number) => {
      if (
        newDuration &&
        newDuration >= (durationList?.[0] ?? 6) &&
        newDuration <= (durationList?.[durationList.length - 1] ?? 30) &&
        campaignFiles?.asset &&
        campaignFiles?.assetType?.startsWith('image/')
      ) {
        uploadFileQueue.clear();

        const uploadPromise = new Promise((resolve, reject) => {
          uploadFileQueue.add(async () => {
            try {
              const uploadResponse = await uploadFile(
                campaignFiles.asset,
                newDuration,
              );

              if (!uploadResponse) {
                reject(
                  new Error('Upload response is null, upload possibly failed'),
                );
              } else if (uploadResponse.error) {
                reject(new Error(uploadResponse.error));
              } else {
                resolve(uploadResponse);
              }
            } catch (error) {
              reject(error);
            }
          });
        });

        toast.promise(uploadPromise, {
          error: (err) => err?.message ?? 'Upload failed',
          loading: 'Uploading file...',
          success: 'File uploaded successfully',
        });
      }
    },
    [campaignFiles, uploadFileQueue],
  );

  const handleFile = (file: File) => {
    handleFileError('clear-error', file.name);

    if (file.size > MAX_FILE_SIZE_IN_BYTES) {
      handleFileError('invalid-file-size', file.name);
      return;
    }

    if (!isSupportedAsset(file.type)) {
      handleFileError('unsupported-file-type', file.name);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const requiredAspectRatio = Array.from(
      getSelectedZoneRatios(
        selectedZones,
        selectedZonePlacements,
        campaignZonePlacementList ?? [],
      ),
    )[0];

    const onMediaLoad = async (
      width: number,
      height: number,
      duration?: number,
    ) => {
      setFileDimension(`${width} x ${height}`);
      setVideoDuration(duration);

      const aspectRatio = getAdjustedAspectRatioString(width, height);

      if (
        duration !== undefined &&
        (duration < (durationList?.[0] ?? 6) ||
          duration > (durationList?.[durationList.length - 1] ?? 30) + 0.99)
      ) {
        handleFileError('invalid-video-duration', file.name);
        URL.revokeObjectURL(objectUrl);
        return;
      }
      if (aspectRatio !== requiredAspectRatio) {
        handleFileError('invalid-asset-aspect-ratio-for-store', file.name);
        URL.revokeObjectURL(objectUrl);
        return;
      }

      if (aspectRatio !== '16:9' && aspectRatio !== '9:16') {
        handleFileError('invalid-asset-aspect-ratio', file.name);
        URL.revokeObjectURL(objectUrl);
        return;
      }

      if (aspectRatio === requiredAspectRatio) {
        if (aspectRatio === '16:9' && (width < 1920 || height < 1080)) {
          handleFileError('invalid-asset-resolution', file.name);
          URL.revokeObjectURL(objectUrl);
          return;
        }

        if (aspectRatio === '9:16' && (width < 1080 || height < 1920)) {
          handleFileError('invalid-asset-resolution', file.name);
          URL.revokeObjectURL(objectUrl);
          return;
        }
      }

      campaignFileUploadField.onChange({
        assetType: file.type,
        assetUrl: objectUrl,
        asset: file,
        assetId: null,
        ...(file.type !== 'video/mp4'
          ? { duration: durationList?.[0] ?? 6 }
          : {}),
      });

      const playDuration =
        file.type.startsWith('video/') && duration ? duration : 6;
      const uploadPromise = new Promise((resolve, reject) => {
        uploadFileQueue.add(async () => {
          try {
            const uploadResponse = await uploadFile(file, playDuration);

            if (!uploadResponse) {
              reject(
                new Error('Upload response is null, upload possibly failed'),
              );
            } else if (uploadResponse.error) {
              reject(new Error(uploadResponse.error));
            } else {
              resolve(uploadResponse);
            }
          } catch (error) {
            reject(error);
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        });
      });

      toast.promise(uploadPromise, {
        error: (err) => err?.message ?? 'Upload failed',
        loading: 'Uploading file...',
        success: 'File uploaded successfully',
      });
    };

    const onMediaError = (mediaType: string) => {
      setError('file', {
        type: 'manual',
        message: `There was an error loading the ${mediaType} file.`,
      });
      URL.revokeObjectURL(objectUrl);
    };

    if (file.type.startsWith('image/')) {
      const image = new Image();
      image.onload = () => onMediaLoad(image.naturalWidth, image.naturalHeight);
      image.onerror = () => onMediaError('image');
      image.src = objectUrl;
    } else if (file.type.startsWith('video/')) {
      const video = document.createElement('video');
      video.onloadedmetadata = () =>
        onMediaLoad(video.videoWidth, video.videoHeight, video.duration);
      video.onerror = () => onMediaError('video');
      video.preload = 'metadata';
      video.src = objectUrl;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleFile(file);
    }
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleFile(file);
    }
  };

  const handleDelete = () => {
    campaignFileUploadField.onChange({
      assetUrl: '',
      assetType: '',
      duration: 0,
      assetId: null,
    });
    onAssetRemoved();
  };

  const ratio = useMemo(
    () =>
      getZoneRatio(
        selectedZones,
        selectedZonePlacements,
        campaignZonePlacementList ?? [],
      ),
    [campaignZonePlacementList, selectedZonePlacements, selectedZones],
  );

  return (
    <div className="mt-6 flex flex-col gap-4 pb-24">
      <div className="flex items-center justify-between">
        <p className="text-lg">
          {isReadOnly ? 'Creative Asset' : 'Upload Creative Asset'}
        </p>
        <AssetGuidelinesModal
          durationList={durationList}
          orientation={ratio}
          labelText="Asset Guidelines"
          isTooltip={true}
        />
      </div>
      {!isReadOnly && (
        <Card className="border-primary bg-primary-100/25 border-2">
          <CardHeader className="gap-1">
            <CardTitle>Need inspiration?</CardTitle>
            <CardDescription>
              Browse our curated promotional templates
            </CardDescription>
            <CardAction className="self-center">
              <Button asChild>
                <a
                  href={PROMO_TEMPLATES_URL}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  View Templates
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            </CardAction>
          </CardHeader>
        </Card>
      )}
      <AssetMethod
        disabled={noZonesSelected}
        handleDelete={handleDelete}
        ratio={noZonesSelected ? undefined : ratio}
        readOnly={Boolean(isReadOnly)}
        setAssetFromLibrary={setAssetFromLibrary}
        showUploadPreview={Boolean(
          campaignFiles?.assetUrl && campaignFiles?.assetType,
        )}
        uploadAsset={
          <CampaignAssetUploader
            accept="image/*,video/*"
            isDragging={isDragging}
            setIsDragging={setIsDragging}
            handleChange={handleChange}
            handleDrop={handleDrop}
            orientation={ratio}
            disabled={noZonesSelected}
          />
        }
        uploadPreview={
          <AssetPreview
            isCampaignEnded={isCampaignEnded}
            assetUrl={campaignFiles?.assetUrl ?? null}
            assetType={campaignFiles?.assetType ?? null}
            assetDimension={fileDimension}
            videoDuration={videoDuration}
            handleDelete={handleDelete}
            onDurationChange={handleFileReupload}
            campaignZonePlacementList={campaignZonePlacementList}
            isReadOnly={isReadOnly}
          />
        }
      />
    </div>
  );
}
